/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { CannedCollectionModule } from '@coursera-web/cds-labs';
import type { CannedCollectionModuleCard, CardSize, ColorChoice } from '@coursera-web/cds-labs';

import { useTracker } from '@coursera/event-pulse/react';

import type { PromoTemplateCannedCollectionFragment } from 'bundles/promo-template/__generated__/contentfulTypes';
import type { NonNullableArray } from 'bundles/promo-template/types';
import CannedCollectionsSchemaMarkup from 'bundles/seo/components/CannedCollectionsSchemaMarkup';

type Props = {
  content: PromoTemplateCannedCollectionFragment & {
    preloadImage?: boolean;
    lazyLoadImage?: boolean;
  };
};

export function CannedCollectionModuleRenderer({ content }: Props) {
  const track = useTracker();

  if (!content) return null;

  const { heading, subheading, cardsCollection, size, color, preloadImage, lazyLoadImage, showAllEntries } = content;
  const cards = cardsCollection?.items ?? [];
  const filteredCards = cards.filter(Boolean) as NonNullableArray<typeof cards>;
  const clickHandler = (card: (typeof filteredCards)[number]) => {
    track('click_button', {
      button: { name: 'promo_template_canned_collection_card', linkURL: card?.target ?? '' },
      pageSection: {
        sectionName: 'canned_collection',
      },
    });
  };

  const itemCards: CannedCollectionModuleCard[] = filteredCards
    .map((card) => {
      const { headline, body, target, ctaText, cardBackgroundImage, eyebrow, eyebrowImage, eyebrowBackground } = card;
      const onClick = () => {
        clickHandler(card);
      };

      // A bit ugly but this removes all Maybe types.
      return {
        id: card.sys.id,
        headline: headline ?? undefined,
        body: body ?? undefined,
        target: target ?? undefined,
        ctaText: ctaText ?? undefined,
        eyebrow: eyebrow ?? undefined,
        eyebrowImage: eyebrowImage ?? undefined,
        eyebrowBackground: eyebrowBackground ?? undefined,
        cardBackgroundImage: {
          width: cardBackgroundImage?.width ?? undefined,
          height: cardBackgroundImage?.height ?? undefined,
          url: cardBackgroundImage?.url ?? undefined,
          title: cardBackgroundImage?.title ?? undefined,
          description: cardBackgroundImage?.description ?? undefined,
          contentType: cardBackgroundImage?.contentType ?? undefined,
        },
        onClick,
      };
    })
    .filter((v) => !!v);

  return (
    <>
      <CannedCollectionModule
        {...content} // Layout related fields
        heading={heading ?? undefined}
        subheading={subheading ?? undefined}
        cards={itemCards}
        size={size as CardSize}
        color={color as ColorChoice}
        preloadImage={!!preloadImage}
        lazyLoadImage={!!lazyLoadImage}
        showAllEntries={!!showAllEntries}
      />
      <CannedCollectionsSchemaMarkup cards={itemCards} headline={heading} description={subheading} />
    </>
  );
}
